import { useEffect, useState } from 'react';
import { GridInitialState } from '@mui/x-data-grid-pro';
import { getUserSubject } from '../../../../../store/User';
import {
  TsdMap,
  fetchTsdMaps,
  getTallyTsdMapsLoadingSubject,
  getTallyTsdMapsSubject
} from '../../../../../store/TsdMap';
import { DefaultTsdMapGridColumnDefs } from '../../ColumnDefs/TsdMapGridColumnDefs';
import { UpstackDataGrid } from '../../../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { getGridColumns, getInitialState } from '../../../../Compounds/UpstackDataGrid/helpers';
import { PageCard } from '../../../../Compounds/CardWithTitle';
import { getDefaultPreferences, GridPreferences } from '../../../../../store/UserCustomizations';
import { Icon } from '../../../../Atoms/Icon';
import { TextSpan } from '../../../../Atoms/Text';
import { GridCustomColDef } from '../../../../types';
import { ComponentSpinner } from '../../../../Compounds/Loading/ComponentSpinner';
import { NewMapDialog } from '../../../../Compounds/Mapping/NewMapDialog/NewMapDialog';
import { EntityType } from '../../../../../store/Search';
import { PageLayout } from '../../../../PageLayout';

export const TsdMapTab = () => {
  const [tsdMapsLoading, setTsdMapsLoading] = useState<boolean>(false);
  const [tsdMaps, setTsdMaps] = useState<TsdMap[]>([]);
  const [tsdMapColumns, setTsdMapColumns] = useState<GridCustomColDef[]>(DefaultTsdMapGridColumnDefs);
  const [isMapFormOpen, setIsMapFormOpen] = useState<boolean>(false);
  const [gridSettings, setGridSettings] = useState<GridPreferences>(
    getDefaultPreferences()?.content['tsdMapGrid'] as GridPreferences
  );
  const [initialState, setInitialState] = useState<GridInitialState>();

  useEffect(() => {
    const loadingSubscription = getTallyTsdMapsLoadingSubject().subscribe((loading) => setTsdMapsLoading(loading));
    const tsdMapsSub = getTallyTsdMapsSubject().subscribe((maps) => setTsdMaps(maps));
    const userSub = getUserSubject().subscribe((usr) => {
      if (usr?.preferences?.content?.tsdMapGrid) {
        setGridSettings(usr.preferences.content.tsdMapGrid);
        const state = getInitialState(usr.preferences.content.tsdMapGrid, DefaultTsdMapGridColumnDefs, 'tsdMapGrid');
        setInitialState(state);
      }
    });

    fetchTsdMaps();

    return () => {
      if (loadingSubscription) loadingSubscription.unsubscribe();
      if (tsdMapsSub) tsdMapsSub.unsubscribe();
      if (userSub) userSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!tsdMapsLoading && tsdMaps.length > 0) {
      setTsdMapColumns(getGridColumns(tsdMaps, DefaultTsdMapGridColumnDefs, 'tsdMapGrid'));
    }
  }, [tsdMapsLoading, tsdMaps.length]);

  const handleNewMap = () => {
    setIsMapFormOpen(true);
  };

  const cardActions = () => (
    <button
      className="flex items-center space-x-2"
      onClick={handleNewMap}
      data-cy="new-tsd-map-button"
      data-testid="new-tsd-map-button">
      <Icon type="plus" />
      <TextSpan
        color="indigo"
        size="sm14">
        New TSD Map
      </TextSpan>
    </button>
  );

  return (
    <PageLayout
      className="w-full"
      dataTestId="tsd-map-page"
      dataCy="tsd-map-page-card"
      pageTitle="">
      <PageCard
        dataTestId="tsd-map-management"
        dataCy="tsd-map-management"
        childrenWrapperClass="min-h-80 max-w-[60vw] min-w-full"
        title="TSD Map"
        cardActions={cardActions()}>
        <>
          {!initialState ||
            (tsdMapsLoading && (
              <div className="flex h-[45rem] m-auto">
                <ComponentSpinner label="Loading data..." />
              </div>
            ))}
          {initialState && !tsdMapsLoading && (
            <UpstackDataGrid
              title="TSD Map"
              rows={tsdMaps}
              columns={tsdMapColumns}
              page="tsdMapGrid"
              gridSettings={gridSettings}
              showSearch={true}
              initialState={initialState}
              apiFilter={false}
              sxStyles={{ borderWidth: '0', borderBottomLeftRadius: '.75rem', borderBottomRightRadius: '.75rem' }}
            />
          )}
          <NewMapDialog
            open={isMapFormOpen}
            setOpen={setIsMapFormOpen}
            mapType={EntityType.TSD}
          />
        </>
      </PageCard>
    </PageLayout>
  );
};
